import { useState, useMemo, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AdminPageHeader,
  AdminSheet,
  AdminEmptyState,
  AdminTableSkeleton,
  DeleteConfirmDialog,
} from './shared';
import { SearchInput } from '@/components/shared/SearchInput';
import { AdfElementPicker } from './AdfElementPicker';
import { useAdminCrud } from '@/hooks/useAdminCrud';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { generateSlug } from '@/lib/utils';
import {
  Pencil,
  Trash2,
  LayoutTemplate,
  GripVertical,
  Car,
  Users,
  Plus,
  X,
  Check,
  Code2,
} from 'lucide-react';

interface AdfTemplate {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  element_slugs: string[];
  enable_trade_in: boolean | null;
  enable_co_buyer: boolean | null;
  is_active: boolean | null;
  display_order: number | null;
  created_at: string | null;
  updated_at: string | null;
}

interface FormData {
  slug: string;
  name: string;
  description: string;
  element_slugs: string[];
  enable_trade_in: boolean;
  enable_co_buyer: boolean;
  is_active: boolean;
  display_order: number;
}

export function AdfTemplatesManager() {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [elementPickerOpen, setElementPickerOpen] = useState(false);
  const [localItems, setLocalItems] = useState<AdfTemplate[] | null>(null);

  const crud = useAdminCrud<AdfTemplate, FormData>({
    table: 'adf_templates',
    select: '*',
    orderBy: { column: 'display_order' },
    entityName: 'template',
    getInitialFormData: () => ({
      slug: '',
      name: '',
      description: '',
      element_slugs: [],
      enable_trade_in: false,
      enable_co_buyer: false,
      is_active: true,
      display_order: 0,
    }),
    mapItemToForm: (item) => ({
      slug: item.slug,
      name: item.name,
      description: item.description || '',
      element_slugs: Array.isArray(item.element_slugs) ? item.element_slugs : [],
      enable_trade_in: item.enable_trade_in ?? false,
      enable_co_buyer: item.enable_co_buyer ?? false,
      is_active: item.is_active ?? true,
      display_order: item.display_order ?? 0,
    }),
    buildPayload: (form) => ({
      slug: form.slug || generateSlug(form.name),
      name: form.name,
      description: form.description || null,
      element_slugs: form.element_slugs,
      enable_trade_in: form.enable_trade_in,
      enable_co_buyer: form.enable_co_buyer,
      is_active: form.is_active,
      display_order: form.display_order,
    }),
    validatePayload: (form) => {
      if (!form.name.trim()) return 'Name is required';
      if (form.element_slugs.length === 0) return 'At least one element must be selected';
      return null;
    },
  });

  // Use local items for optimistic DnD updates, fall back to crud.items
  const items = localItems ?? crud.items;

  // Sync local items when crud.items changes
  useMemo(() => {
    if (crud.items.length > 0 && !localItems) {
      setLocalItems(crud.items);
    }
  }, [crud.items, localItems]);

  // Filter items by search
  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    const term = searchTerm.toLowerCase();
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(term) ||
        item.slug.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term)
    );
  }, [items, searchTerm]);

  // Handle drag end for reordering
  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;

      const reordered = Array.from(items);
      const [movedItem] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, movedItem);

      // Update display_order
      const updatedItems = reordered.map((item, index) => ({
        ...item,
        display_order: index,
      }));

      // Optimistic update
      setLocalItems(updatedItems);

      // Persist to database
      try {
        for (const item of updatedItems) {
          await supabase
            .from('adf_templates')
            .update({ display_order: item.display_order })
            .eq('id', item.id);
        }
      } catch (error) {
        toast({
          title: 'Error reordering templates',
          description: 'Failed to save new order',
          variant: 'destructive',
        });
        // Revert on error
        setLocalItems(null);
        crud.fetchData();
      }
    },
    [items, toast, crud]
  );

  // Handle element selection from picker
  const handleElementSelect = (slugs: string[]) => {
    crud.setFormData((prev) => ({ ...prev, element_slugs: slugs }));
  };

  // Remove a single element from selection
  const removeElement = (slug: string) => {
    crud.setFormData((prev) => ({
      ...prev,
      element_slugs: prev.element_slugs.filter((s) => s !== slug),
    }));
  };

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    crud.setFormData((prev) => ({
      ...prev,
      name,
      slug: crud.editingItem ? prev.slug : generateSlug(name),
    }));
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="ADF Templates"
        onAdd={crud.openCreateEditor}
        addLabel="Add Template"
      />

      {/* Search */}
      <SearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search templates..."
      />

      {/* Table */}
      {crud.loading ? (
        <AdminTableSkeleton columns={5} rows={5} />
      ) : filteredItems.length === 0 ? (
        searchTerm ? (
          <AdminEmptyState
            title="No templates found"
            description="Try adjusting your search"
            icon={LayoutTemplate}
          />
        ) : (
          <AdminEmptyState
            title="No templates yet"
            description="Create your first ADF template"
            icon={LayoutTemplate}
            actionLabel="Add Template"
            onAction={crud.openCreateEditor}
          />
        )
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="templates">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="border rounded-lg"
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10" />
                      <TableHead>Name</TableHead>
                      <TableHead className="text-center">Elements</TableHead>
                      <TableHead className="text-center">Trade-In</TableHead>
                      <TableHead className="text-center">Co-Buyer</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="w-24" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item, index) => (
                      <Draggable key={item.id} draggableId={item.id} index={index}>
                        {(dragProvided, snapshot) => (
                          <TableRow
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className={snapshot.isDragging ? 'bg-muted' : ''}
                          >
                            <TableCell>
                              <div
                                {...dragProvided.dragHandleProps}
                                className="cursor-grab active:cursor-grabbing"
                              >
                                <GripVertical className="h-4 w-4 text-muted-foreground" />
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{item.name}</p>
                                <p className="text-xs text-muted-foreground">{item.slug}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="secondary">
                                {item.element_slugs?.length || 0}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {item.enable_trade_in ? (
                                <Car className="h-4 w-4 mx-auto text-primary" />
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {item.enable_co_buyer ? (
                                <Users className="h-4 w-4 mx-auto text-primary" />
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={item.is_active ? 'default' : 'secondary'}>
                                {item.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => crud.openEditEditor(item)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => crud.confirmDelete(item)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Editor Sheet */}
      <AdminSheet
        open={crud.editorOpen}
        onOpenChange={crud.setEditorOpen}
        title={crud.editingItem ? 'Edit Template' : 'New Template'}
        description="Configure a pre-built template for the ADF Builder"
        onSave={crud.handleSave}
        isSaving={crud.isSaving}
      >
        <div className="space-y-6">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={crud.formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g., Basic Contact"
            />
          </div>

          {/* Slug */}
          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={crud.formData.slug}
              onChange={(e) =>
                crud.setFormData((prev) => ({ ...prev, slug: e.target.value }))
              }
              placeholder="auto-generated-from-name"
            />
            <p className="text-xs text-muted-foreground">
              URL-safe identifier (auto-generated if empty)
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={crud.formData.description}
              onChange={(e) =>
                crud.setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder="Brief description of this template's use case"
              rows={2}
            />
          </div>

          {/* Elements */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Elements *</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setElementPickerOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Select Elements
              </Button>
            </div>
            {crud.formData.element_slugs.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 p-3 border rounded-md bg-muted/30 max-h-40 overflow-y-auto">
                {crud.formData.element_slugs.map((slug) => (
                  <Badge key={slug} variant="secondary" className="gap-1">
                    <Code2 className="h-3 w-3" />
                    {slug}
                    <button
                      type="button"
                      onClick={() => removeElement(slug)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground p-3 border rounded-md border-dashed text-center">
                No elements selected. Click "Select Elements" to add.
              </p>
            )}
          </div>

          {/* Toggles */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label
                  htmlFor="enable-trade-in"
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Car className="h-4 w-4 text-muted-foreground" />
                  Enable Trade-In Vehicle
                </Label>
                <p className="text-xs text-muted-foreground">
                  Adds second &lt;vehicle&gt; with trade-in alternate values
                </p>
              </div>
              <Switch
                id="enable-trade-in"
                checked={crud.formData.enable_trade_in}
                onCheckedChange={(checked) =>
                  crud.setFormData((prev) => ({ ...prev, enable_trade_in: checked }))
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label
                  htmlFor="enable-co-buyer"
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Users className="h-4 w-4 text-muted-foreground" />
                  Enable Co-Buyer
                </Label>
                <p className="text-xs text-muted-foreground">
                  Adds second &lt;customer&gt; with co-buyer alternate values
                </p>
              </div>
              <Switch
                id="enable-co-buyer"
                checked={crud.formData.enable_co_buyer}
                onCheckedChange={(checked) =>
                  crud.setFormData((prev) => ({ ...prev, enable_co_buyer: checked }))
                }
              />
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div className="space-y-0.5">
                <Label htmlFor="is-active" className="flex items-center gap-2 cursor-pointer">
                  <Check className="h-4 w-4 text-muted-foreground" />
                  Active
                </Label>
                <p className="text-xs text-muted-foreground">
                  Inactive templates won't appear in the builder
                </p>
              </div>
              <Switch
                id="is-active"
                checked={crud.formData.is_active}
                onCheckedChange={(checked) =>
                  crud.setFormData((prev) => ({ ...prev, is_active: checked }))
                }
              />
            </div>
          </div>
        </div>
      </AdminSheet>

      {/* Element Picker Dialog */}
      <AdfElementPicker
        open={elementPickerOpen}
        onOpenChange={setElementPickerOpen}
        onSelect={handleElementSelect}
        selectedSlugs={crud.formData.element_slugs}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={crud.deleteDialogOpen}
        onOpenChange={crud.setDeleteDialogOpen}
        onConfirm={crud.handleDelete}
        isDeleting={crud.isDeleting}
        itemName={crud.itemToDelete?.name}
        itemType="template"
      />
    </div>
  );
}
